import React, { useEffect, useMemo } from "react";
import { useQuery } from "@apollo/client";
import { Select } from "@outpave/ds";
import type { SelectOption } from "@outpave/ds";
import { GET_ADDRESSES } from "../../graphql/address";
import AddressStatus from "enums/AddressStatus";

interface Location {
  id: string;
  addressName: string;
  defaultLocation?: boolean;
}

interface LocationSearchAutoCompleteProps {
  location: Location | null;
  errorText?: string;
  helperText?: string;
  label?: string;
  onSelectLocation: (location: Location | null) => void;
  /** Receives the company default the picker fills in; without it, the default goes to `onSelectLocation`. */
  onDefaultLocation?: (location: Location) => void;
  companyId?: string;
}

/**
 * The company location picker. Every active address is fetched at once and
 * filtering is local, so this is a plain `searchable` Select and the DS marks
 * the matched runs itself.
 *
 * Required and un-clearable: a spend always belongs to a location, which is why
 * the effect below falls back to the company default.
 */
const LocationSearchAutoComplete: React.FC<LocationSearchAutoCompleteProps> = ({
  location,
  errorText,
  helperText,
  label,
  onSelectLocation,
  onDefaultLocation,
  companyId,
}) => {
  const { data, loading } = useQuery(GET_ADDRESSES, {
    variables: {
      status: AddressStatus.ACTIVE,
      ...(companyId ? { companyId } : {}),
    },
  });

  const locations: Location[] = useMemo(
    () =>
      (data?.addresses?.edges ?? []).map(({ node }: { node: Location }) => ({
        id: node?.id,
        addressName: node?.addressName,
        defaultLocation: node?.defaultLocation,
      })),
    [data],
  );

  // Reading the query result directly retires the `queueMicrotask` the old
  // extractor needed: it was setting state from inside a render-path callback,
  // and deferring was the only way to stop React throwing.
  useEffect(() => {
    const fallback = locations.find((loc) => loc.defaultLocation);
    if (!location?.id && fallback) {
      (onDefaultLocation ?? onSelectLocation)({
        id: fallback.id,
        addressName: fallback.addressName,
      });
    }
    // Only run when locations change or location changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locations, location]);

  const options: SelectOption[] = useMemo(() => {
    const listed = locations.map((loc) => ({
      id: loc.id,
      label: loc.addressName,
    }));
    // The DS resolves a value's label only by finding its id in `options`, so a
    // saved location renders blank until the query lands. Seeding it keeps the
    // field showing its selection from the first paint.
    if (!location?.id || listed.some((o) => o.id === location.id))
      return listed;
    return [{ id: location.id, label: location.addressName }, ...listed];
  }, [locations, location]);

  return (
    <Select
      label={label}
      aria-label={label ? undefined : "Location"}
      searchable
      required
      loading={loading}
      errorText={errorText}
      helperText={helperText}
      options={options}
      value={location?.id ?? null}
      // The seeded selection is not in `locations` until the query lands, and
      // a null here would hand the field to the default-location effect.
      onChange={(id) =>
        onSelectLocation(
          locations.find((loc) => loc.id === id) ??
            (location?.id === id ? location : null),
        )
      }
    />
  );
};

export default LocationSearchAutoComplete;
